/**
 * Meter-first landing method chooser — pure domain / validation helpers.
 *
 * The storefront's front door now asks the shopper how they want to identify
 * their meter — by their ERCOT ESI ID, or by ZIP → address (the epic §3 target
 * flow). Both sub-flows resolve to the SAME `ServiceabilityCandidate` on the
 * BFF; the choice only affects which lookup we run.
 *
 * Kept OUT of the .tsx per CLAUDE.md: the tab tokens, the i18n keys for their
 * labels, ZIP shape validation, and the "cold-load default tab" decision all
 * live here so components stay render-only. The decision (2026-07-28) is that
 * method persistence is in-memory only — no URL param, no storage — so cold
 * load defaults to "Shop by ZIP".
 */

/**
 * The two meter-resolution paths the landing chooser offers.
 *
 * `esiid` — the shopper types their 17–22 digit ERCOT ESI ID directly.
 *           Captcha- + rate-limited on the BFF; the ESI ID never becomes a
 *           browser-held redemption key (AC3 — the funnel session still keeps
 *           it server-side, the browser holds the opaque `candidateId`).
 * `zip`   — the shopper enters ZIP, picks a serviceable address from a
 *           suggest-populated list, then the BFF resolves the ESI ID
 *           server-side and hands back a `candidateId`.
 */
export type FunnelMethod = 'esiid' | 'zip';

/** All valid methods, in the order the chooser tabs render. */
export const FUNNEL_METHODS: readonly FunnelMethod[] = ['esiid', 'zip'] as const;

/**
 * Default on cold load. `zip` today because the ESI ID sub-flow depends on the
 * SHOP-1 backend route that isn't wired yet (see the epic §5). Flipping this
 * later is a one-line change once ESI ID lookup lands.
 */
export const DEFAULT_FUNNEL_METHOD: FunnelMethod = 'zip';

/**
 * i18n key for the tab label of a method — always resolved through `t()`.
 * Kept as a pure mapping so the tab component stays declarative.
 */
export function funnelMethodLabelKey(method: FunnelMethod): string {
  return method === 'esiid' ? 'shop.landing.method.esiid' : 'shop.landing.method.zip';
}

/**
 * Runtime guard so a caller can safely widen an unknown string into a
 * FunnelMethod (e.g. reading from a stored value in a future iteration). Today
 * it protects the setter from being called with a bad tab value.
 */
export function isFunnelMethod(value: unknown): value is FunnelMethod {
  return value === 'esiid' || value === 'zip';
}

/**
 * A tightly-shaped 5-digit ZIP check. Reused by the ZIP sub-flow to decide
 * when the address-suggest query may fire (and when the address `Select`
 * becomes enabled). Deliberately narrower than the `ServiceAddressSchema` ZIP
 * regex prose — it never accepts a non-string, matches only the exact form,
 * and never allows a ZIP+4 (Texas retail collects the 5-digit only).
 */
export function isValidZip(value: string): boolean {
  return /^\d{5}$/u.test(value);
}
